import { useCallback } from 'react';
import type { SecretConfidence } from '@kirin/api-client/helpers/secret-confidence';
import {
  useAccountSecretConfidenceThreshold,
  useUpsertAccountSecretConfidenceThreshold,
} from '@/hooks/api/useAccountSettings';
import { useToast } from '@/hooks/use-toast';

type Result = {
  confidence: SecretConfidence;
  isLoading: boolean;
  hasError: boolean;
  isSaving: boolean;
  onChange: (next: SecretConfidence) => Promise<void>;
};

export function usePersonalSecretConfidence(enabled: boolean): Result {
  const { toast } = useToast();
  const query = useAccountSecretConfidenceThreshold(enabled);
  const mutation = useUpsertAccountSecretConfidenceThreshold();

  const onChange = useCallback(
    async (confidence: SecretConfidence): Promise<void> => {
      try {
        await mutation.mutateAsync({ confidence });
        toast({
          title: 'Settings saved',
          description: 'Secret alert confidence threshold updated successfully',
        });
      } catch {
        toast({
          variant: 'destructive',
          title: 'Failed to update',
          description: 'Could not save secret alert confidence threshold',
        });
      }
    },
    [mutation, toast]
  );

  return {
    confidence: query.confidence ?? 'high',
    isLoading: query.isPending,
    hasError: query.isError,
    isSaving: mutation.isPending,
    onChange,
  };
}
